import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ConfettiExplosion from "react-confetti-explosion";
import BubbleEffect from "../../animations/BubbleEffect";
import { useNavigate } from "react-router";
import successOrderSound from "../../assets/audio/transaction_success.mp3";
import failureOrderSound from "../../assets/audio/transaction_failed.mp3";

import { useAudio } from "../../hooks/useAudio";
// import TruckButton from "../profile/Truck";

const CheckoutResult = forwardRef(({ data }, ref) => {
  const isSuccess = data?.orderId != null;
  // const truckRef = useRef(null);

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const successSound = useAudio(successOrderSound);
  const failureSound = useAudio(failureOrderSound);

  useEffect(() => {
    if (!open) return;

    const timeout = setTimeout(() => {
      if (isSuccess) {
        // truckRef.current?.playAnimation();
        successSound.play();
      } else {
        failureSound.play();
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [open]);
  useImperativeHandle(ref, () => ({
    openDialog: () => setOpen(true),
    closeDialog: () => setOpen(false),
  }));

  const largeExplosion = {
    force: 0.8,
    duration: 3000,
    particleCount: 250,
    width: 1600,
  };

  if (!data) {
    return null;
  }
  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        fullWidth
        open={open}
        maxWidth="md"
        onClose={() => setOpen(false)}
        sx={dialogStyles}
        disableEscapeKeyDown={true}
      >
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 4,
              mt: 3,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BubbleEffect isSuccess={isSuccess} />
            {isSuccess && <ConfettiExplosion {...largeExplosion} />}
            {/* <Typography
              sx={{
                mt: 5,
                borderRadius: "8px",
              }}
            >
              {isSuccess && <TruckButton ref={truckRef} />}
            </Typography> */}
            <Typography
              sx={{
                mt: 4,
                fontWeight: 600,  
                fontSize: "20px",
                textAlign: "center",
                color: "var(--neutral-black)",
              }}
            >
              {isSuccess ? "Congratulations" : "Oops!"}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                textAlign: "center",
                color: isSuccess
                  ? "var(--neutral-800)"
                  : "var(--primary-red-base)",
              }}
            >
              {isSuccess ? "Your order is submitted!" : "Something went wrong."}
            </Typography>

            {isSuccess && (
              <>
                <Typography
                  sx={{
                    mt: 3,
                    fontWeight: 500,
                    fontSize: "16px",
                    textAlign: "center",
                    color: "var(--neutral-700)",
                  }}
                >
                  Order Number :
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      color: "var(--neutral-black)",
                    }}
                  >
                    {` #${data?.orderId}`}
                  </Typography>
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    textAlign: "center",
                    color: "var(--neutral-800)",
                  }}
                >
                  Your order has been placed successfully. Go to order page to
                  manage status.
                </Typography>
              </>
            )}

            {!isSuccess && (
              <Typography
                sx={{
                  mt: 2,
                  fontWeight: 500,
                  fontSize: "16px",
                  textAlign: "center",
                  color: "var(--neutral-700)",
                }}
              >
                {data?.errorMsg ||
                  "We couldn't process your order. Please try again or contact support."}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                mt: 4,
                mx: 5,
                gap: 5,
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={isSuccess ? () => navigate("/") : () => setOpen(false)}
                variant="outlined"
                sx={{
                  py: 1,
                  fontSize: "14px",
                  fontWeight: 600,
                  border: "1px solid var(--primary-red-base)",
                  borderRadius: "8px ",
                }}
              >
                {isSuccess ? "Continue shopping" : "Try Again"}
              </Button>

              {isSuccess && (
                <Button
                  onClick={() => navigate("/account/my-orders")}
                  variant="outlined"
                  sx={{
                    py: 1,
                    fontSize: "14px",
                    fontWeight: 600,
                    backgroundColor: "var(--primary-red-base)",
                    color: "white",
                    borderRadius: "8px ",
                  }}
                >
                  My Order
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
});

// Styles
const dialogStyles = {
  "& .MuiPaper-root": {
    backgroundColor: "var(--neutral-100)",
    borderRadius: "8px",
  },
};

export default CheckoutResult;
